import { Link, useNavigate } from 'react-router-dom';
import { FaBackward } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import {
  getUserById,
  createPreference,
  getCountries,
  getStates,
  getCities,
  getReligions,
  getMotherTongue,
  getEducation,
  getOccupations
} from '../utils/api';
import { toast } from 'react-toastify';
import { FadeLoader } from 'react-spinners';

export default function MatrimonyPreference() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasFounder, setHasFounder] = useState(false);
  const animatedComponents = makeAnimated();

  const [formData, setFormData] = useState({
    lookingFor: '',
    ageRange: { min: '', max: '' },
    motherTongue: [],
    education: [],
    occupation: [],
    annualIncome: { min: '', max: '' },
    religion: '',
    country: '',
    state: '',
    city: '',
    lifestylePreferences: []
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
    states: [],
    cities: [],
    religions: [],
    motherTongues: [],
    educationList: [],
    occupations: [],
    lifestyleOptions: [
      { value: 'Vegetarian', label: 'Vegetarian' },
      { value: 'Non-Vegetarian', label: 'Non-Vegetarian' },
      { value: 'Vegan', label: 'Vegan' },
      { value: 'Smoking', label: 'Smoking' },
      { value: 'Non-Smoking', label: 'Non-Smoking' },
      { value: 'Drinking', label: 'Drinking' },
      { value: 'Non-Drinking', label: 'Non-Drinking' }
    ]
  });

  // Load user and their founder status
  useEffect(() => {
    async function loadUser() {
      const userId = localStorage.getItem('matrimonyUserId');
      if (!userId) return;
      try {
        const user = await getUserById(userId);
        setHasFounder(!!user.founder_id?.founderId);

        // Prefill preferences if they exist
        const pref = user.preference || {};
        setFormData({
          lookingFor: pref.lookingFor || '',
          ageRange: { min: pref.ageRange?.min || '', max: pref.ageRange?.max || '' },
          motherTongue: Array.isArray(pref.motherTongue) ? pref.motherTongue : [pref.motherTongue].filter(Boolean),
          education: Array.isArray(pref.education) ? pref.education : [pref.education].filter(Boolean),
          occupation: Array.isArray(pref.occupation) ? pref.occupation : [pref.occupation].filter(Boolean),
          annualIncome: {
            min: pref.annualIncome?.min ? pref.annualIncome.min / 100000 : '',
            max: pref.annualIncome?.max ? pref.annualIncome.max / 100000 : ''
          },
          religion: pref.religion || '',
          country: pref.country || '',
          state: pref.state || '',
          city: pref.city || '',
          lifestylePreferences: Array.isArray(pref.lifestylePreferences) ? pref.lifestylePreferences : [pref.lifestylePreferences].filter(Boolean)
        });
      } catch (err) {
        console.error('Failed to load user:', err);
      }
    }
    loadUser();
  }, []);

  // Load dropdowns
  useEffect(() => {
    async function fetchDropdowns() {
      try {
        const [countries, religions, motherTongues, educationList, occupations] = await Promise.all([
          getCountries(),
          getReligions(),
          getMotherTongue(),
          getEducation(),
          getOccupations()
        ]);
        setDropdowns(prev => ({ 
          ...prev, 
          countries, 
          religions, 
          motherTongues, 
          educationList, 
          occupations 
        }));
      } catch (err) {
        console.error(err);
      }
    }
    fetchDropdowns();
  }, []);

  // Load states when country changes
  useEffect(() => {
    async function fetchStates() {
      if (!formData.country) return;
      try {
        const states = await getStates(formData.country);
        setDropdowns(prev => ({ ...prev, states }));
        setFormData(prev => ({ ...prev, state: '', city: '' }));
      } catch (err) {
        console.error(err);
      }
    }
    fetchStates();
  }, [formData.country]);

  // Load cities when state changes
  useEffect(() => {
    async function fetchCities() {
      if (!formData.state) return;
      try {
        const cities = await getCities(formData.state);
        setDropdowns(prev => ({ ...prev, cities }));
        setFormData(prev => ({ ...prev, city: '' }));
      } catch (err) {
        console.error(err);
      }
    }
    fetchCities();
  }, [formData.state]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRangeChange = e => {
    const { name, value } = e.target;
    const [field, bound] = name.split('.');
    setFormData(prev => ({ 
      ...prev, 
      [field]: { ...prev[field], [bound]: value } 
    }));
  };

  const handleMultiSelectChange = (field) => (selectedOptions) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: selectedOptions.map(opt => opt.value) 
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      user: localStorage.getItem('matrimonyUserId'),
      lookingFor: formData.lookingFor,
      ageRange: { 
        min: Number(formData.ageRange.min), 
        max: Number(formData.ageRange.max) 
      },
      motherTongue: formData.motherTongue,
      education: formData.education,
      occupation: formData.occupation,
      annualIncome: {
        min: Number(formData.annualIncome.min) * 100000,
        max: Number(formData.annualIncome.max) * 100000
      },
      religion: formData.religion,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      lifestylePreferences: formData.lifestylePreferences,
    };

    try {
      await createPreference(payload);
      toast.success('Preferences saved successfully');
      navigate('/matrimony');
    } catch (err) {
      toast.error(err.message || 'Failed to save preferences');
      console.error('Error saving preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectClass = 'w-full h-12 bg-gray-100 rounded-full px-4 focus:outline-none focus:ring-2 focus:ring-indigo-300 border-none';

  return (
    <div className="w-full h-screen bg-gradient-to-br from-red-500 via-red-300 to-yellow-100 flex items-center justify-center p-4 overflow-hidden">
      <div className="w-full h-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
        <div className="md:w-1/2 w-full bg-white px-8 py-6 h-full overflow-y-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center pt-4">Preference</h1>
          {loading && (
            <div className="flex justify-center my-4">
              <FadeLoader color="#4b154c" height={12} width={4} radius={2} />
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5 text-sm mx-auto">
            {/* Looking For */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">Looking for</label>
              <select 
                name="lookingFor" 
                value={formData.lookingFor} 
                onChange={handleChange} 
                className={selectClass}
                required
              >
                <option value="">Select Gender</option>
                <option value="Bride">Bride</option>
                <option value="Groom">Groom</option>
              </select>
            </div>

            {/* Age Range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block mb-2 text-gray-700 font-medium">Min Age</label>
                <select 
                  name="ageRange.min" 
                  value={formData.ageRange.min} 
                  onChange={handleRangeChange} 
                  className={selectClass}
                  required
                >
                  <option value="">Select Min Age</option>
                  {[18, 20, 22, 25, 28, 30, 32, 35].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-gray-700 font-medium">Max Age</label>
                <select 
                  name="ageRange.max" 
                  value={formData.ageRange.max} 
                  onChange={handleRangeChange} 
                  className={selectClass}
                  required
                >
                  <option value="">Select Max Age</option>
                  {[25, 28, 30, 32, 35, 38, 40, 45].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Religion */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">Religion</label>
              <select
                name="religion"
                value={formData.religion}
                onChange={handleChange}
                className={selectClass}
                required
              >
                <option value="">Select Religion</option>
                {dropdowns.religions.map(r => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Mother Tongue (Multi-select) */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">Mother Tongue</label>
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={dropdowns.motherTongues.map(mt => ({
                  value: mt._id,
                  label: mt.name
                }))}
                value={formData.motherTongue.map(mt => ({
                  value: mt,
                  label: dropdowns.motherTongues.find(m => m._id === mt)?.name || mt
                }))}
                onChange={handleMultiSelectChange('motherTongue')}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Select Mother Tongue(s)"
              />
            </div>

            {/* Education (Multi-select) */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">Education</label>
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={dropdowns.educationList.map(ed => ({
                  value: ed._id,
                  label: ed.name
                }))}
                value={formData.education.map(ed => ({
                  value: ed,
                  label: dropdowns.educationList.find(e => e._id === ed)?.name || ed
                }))}
                onChange={handleMultiSelectChange('education')}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Select Education(s)"
              />
            </div>

            {/* Occupation (only for founders) */}
            {hasFounder && (
              <div>
                <label className="block mb-2 text-gray-700 font-medium">Occupation</label>
                <Select
                  closeMenuOnSelect={false}
                  components={animatedComponents}
                  isMulti
                  options={dropdowns.occupations.map(o => ({
                    value: o._id,
                    label: o.title
                  }))}
                  value={formData.occupation.map(occ => ({
                    value: occ,
                    label: dropdowns.occupations.find(o => o._id === occ)?.title || occ
                  }))}
                  onChange={handleMultiSelectChange('occupation')}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select Occupation(s)"
                />
              </div>
            )}

            {/* Annual Income */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block mb-2 text-gray-700 font-medium">Income Min (Lakhs)</label>
                <select 
                  name="annualIncome.min" 
                  value={formData.annualIncome.min} 
                  onChange={handleRangeChange} 
                  className={selectClass}
                >
                  <option value="">Select Min</option>
                  {[0, 2, 5, 10, 15, 20].map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-gray-700 font-medium">Income Max (Lakhs)</label>
                <select 
                  name="annualIncome.max" 
                  value={formData.annualIncome.max} 
                  onChange={handleRangeChange} 
                  className={selectClass}
                >
                  <option value="">Select Max</option>
                  {[5, 10, 15, 20, 25, 30].map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location - Country, State, City */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="block mb-2 text-gray-700 font-medium">Country</label>
                <select 
                  name="country" 
                  value={formData.country} 
                  onChange={handleChange} 
                  className={selectClass}
                >
                  <option value="">Select Country</option>
                  {dropdowns.countries.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-gray-700 font-medium">State</label>
                <select 
                  name="state" 
                  value={formData.state} 
                  onChange={handleChange} 
                  className={selectClass}
                  disabled={!formData.country}
                >
                  <option value="">Select State</option>
                  {dropdowns.states.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 text-gray-700 font-medium">City</label>
                <select 
                  name="city" 
                  value={formData.city} 
                  onChange={handleChange} 
                  className={selectClass}
                  disabled={!formData.state}
                >
                  <option value="">Select City</option>
                  {dropdowns.cities.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Lifestyle Preferences (Multi-select) */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">Lifestyle Preferences</label>
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={dropdowns.lifestyleOptions}
                value={formData.lifestylePreferences.map(pref => ({
                  value: pref,
                  label: pref
                }))}
                onChange={handleMultiSelectChange('lifestylePreferences')}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder="Select Lifestyle Preferences"
              />
            </div>

            <div className="flex justify-center pt-4">
              <button
                type="submit"
                className="w-64 h-12 bg-[#4b154c] text-white font-medium rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </form>
        </div>
        <div className="md:w-1/2 w-full hidden lg:block relative">
          <img src="/register.png" alt="Illustration" className="w-full h-full object-cover" />
          <Link to="/" className="absolute top-4 right-4 flex items-center text-white">
            <FaBackward className="mr-1" /> Back
          </Link>
        </div>
      </div>
    </div>
  );
}